import faceSheetsApi, { type FaceSheetSearchParams, type FaceSheetStatusFlags, type SavePayloadBody } from "../../../api/face_sheets";
import type { FaceSheetDetailResult, FaceSheetListResult, FaceSheetSummary } from "./types";

// フェイスシートのデータ取得・更新アクション。
// Vuex を介さず、api/face_sheets.ts（共有データアクセス層）をラップして
// プレーンなデータを返す。呼び出し側の Page がローカル state を保持する。
//
// 既存の features/daytime-advice-search（createSearchActions）と同じく、
// ファクトリ関数で「アクションの集合」を生成する FSD ネイティブなパターンを踏襲する。

// 楽観的ロック競合時にユーザーへ提示する共通文言。
export const OPTIMISTIC_LOCK_ALERT_MESSAGE =
  "他の方が同時に本データを編集し、あなたの編集・保存の前に本データが更新されました。お手数ですが、OKボタンを押下後、最新のデータを確認し、入力をやり直してください。";

// ハイリスク権限がない場合にユーザーへ提示する共通文言。
export const PERMISSION_DENIED_MESSAGE =
  "このページを表示する権限がありません。トップページへ移動します。";

// ネットワークエラー等、楽観的ロック競合・権限エラー以外の汎用エラー時にユーザーへ提示する共通文言。
export const GENERIC_ERROR_MESSAGE =
  "エラーが発生しました。時間をおいてもう一度お試しください。";

const isStaleObjectError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
  return response?.status === 409 || response?.data?.error === "stale_object";
};

// サーバー側のハイリスク権限チェック（head :forbidden）を検知する。
export const isForbiddenError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number } })?.response;
  return response?.status === 403;
};

export type FaceSheetsActions = {
  fetchList(params?: FaceSheetSearchParams): Promise<FaceSheetListResult | null>;
  fetchDetail(userId: string): Promise<FaceSheetDetailResult | null>;
  updateStatus(userId: string, statusFlags: FaceSheetStatusFlags, lockVersion: number): Promise<FaceSheetSummary | null>;
  saveSpecialNote(userId: string, specialNote: string, baseRevisionId: string | null): Promise<FaceSheetDetailResult | null>;
  saveExternalLink(userId: string, item: SavePayloadBody, isNew: boolean): Promise<FaceSheetDetailResult | null>;
  saveFrequentSupport(userId: string, item: SavePayloadBody, isNew: boolean): Promise<FaceSheetDetailResult | null>;
  saveHighRiskAlertSupport(userId: string, item: SavePayloadBody): Promise<FaceSheetDetailResult | null>;
  saveMunicipalityCooperation(userId: string, item: SavePayloadBody, isNew: boolean): Promise<FaceSheetDetailResult | null>;
  deleteExternalLink(userId: string, id: string): Promise<FaceSheetDetailResult | null>;
  deleteFrequentSupport(userId: string, id: string): Promise<FaceSheetDetailResult | null>;
  deleteMunicipalityCooperation(userId: string, id: string): Promise<FaceSheetDetailResult | null>;
};

// onOptimisticLockConflict: 楽観的ロック競合（409）検知時に呼ばれるコールバック。
// onGenericError: 権限エラー（403、呼び出し側でリダイレクト処理するため再送出する）・
//   楽観的ロック競合以外の全エラー（ネットワークエラー等）を検知した際に呼ばれるコールバック。
// トーストなどの UI は呼び出し側（Page）が担い、actions 層は UI を持たない。
export const createFaceSheetsActions = (
  onOptimisticLockConflict?: () => void,
  onGenericError?: () => void
): FaceSheetsActions => {
  // 全アクション共通のエラーハンドリング。
  // - 楽観的ロック競合（409）: コールバックを呼び、null を返す（呼び出し側は null で早期 return）。
  // - 権限エラー（403）: 呼び出し側のリダイレクト処理に委ねるため、そのまま再送出する。
  // - それ以外（ネットワークエラー等）: 汎用エラーコールバックを呼び、null を返す。
  const guardErrors = async <T>(task: () => Promise<T>): Promise<T | null> => {
    try {
      return await task();
    } catch (error) {
      if (isStaleObjectError(error)) {
        if (onOptimisticLockConflict) {
          onOptimisticLockConflict();
        }
        return null;
      }
      if (isForbiddenError(error)) {
        throw error;
      }
      if (onGenericError) {
        onGenericError();
      }
      return null;
    }
  };

  return {
    fetchList(params) {
      return guardErrors(() => faceSheetsApi.fetchFaceSheets(params));
    },
    fetchDetail(userId) {
      return guardErrors(() => faceSheetsApi.fetchFaceSheetDetail(userId));
    },
    updateStatus(userId, statusFlags, lockVersion) {
      return guardErrors(() => faceSheetsApi.updateStatus(userId, statusFlags, lockVersion));
    },
    saveSpecialNote(userId, specialNote, baseRevisionId) {
      return guardErrors(async () => {
        await faceSheetsApi.saveSpecialNote(userId, specialNote, baseRevisionId);
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    saveExternalLink(userId, item, isNew) {
      return guardErrors(async () => {
        if (isNew) {
          await faceSheetsApi.createExternalLink(userId, item);
        } else {
          await faceSheetsApi.updateExternalLink(userId, String(item.id), item);
        }
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    saveFrequentSupport(userId, item, isNew) {
      return guardErrors(async () => {
        if (isNew) {
          await faceSheetsApi.createFrequentSupport(userId, item);
        } else {
          await faceSheetsApi.updateFrequentSupport(userId, String(item.id), item);
        }
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    saveHighRiskAlertSupport(userId, item) {
      return guardErrors(async () => {
        await faceSheetsApi.updateAlertSupport(userId, String(item.id), item);
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    saveMunicipalityCooperation(userId, item, isNew) {
      return guardErrors(async () => {
        if (isNew) {
          await faceSheetsApi.createMunicipalCooperation(userId, item);
        } else {
          await faceSheetsApi.updateMunicipalCooperation(userId, String(item.id), item);
        }
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    deleteExternalLink(userId, id) {
      return guardErrors(async () => {
        await faceSheetsApi.deleteExternalLink(userId, id);
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    deleteFrequentSupport(userId, id) {
      return guardErrors(async () => {
        await faceSheetsApi.deleteFrequentSupport(userId, id);
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    },
    deleteMunicipalityCooperation(userId, id) {
      return guardErrors(async () => {
        await faceSheetsApi.deleteMunicipalCooperation(userId, id);
        return faceSheetsApi.fetchFaceSheetDetail(userId);
      });
    }
  };
};
